import axios from "axios";
import { joinQueryParams } from '../utils/urls.js'

// gis2TokenStorageId for storing token in local storage.
//
// We have standardised "gis2PortalToken" as the storage name so you can go
// go from one 'apps.london.gov.uk' service to another without needing to
// login to each separately.
const gis2TokenStorageId = "gis2PortalToken"

const authCheckUrlQueryParams = {
	where: "1=1",
	text: "",
	objectIds: "",
	time: "",
	geometry: "",
	geometryType: "esriGeometryEnvelope",
	inSR: "",
	spatialRel: "esriSpatialRelIntersects",
	relationParam: "",
	returnGeometry: true,
	returnTrueCurves: false,
	maxAllowableOffset: "",
	geometryPrecision: "",
	outSR: "",
	returnIdsOnly: false,
	returnCountOnly: false,
	groupByFieldsForStatistics: "",
	outStatistics: "",
	returnZ: false,
	returnM: false,
	gdbVersion: "",
	returnDistinctValues: false,
	resultOffset: "",
	resultRecordCount: 1,
	queryByDistance: "",
	returnExtentsOnly: false,
	datumTransformation: "",
	parameterValues: "",
	rangeValues: "",
	f: "pjson",
}

export const putGis2TokenIntoStorage = (token) => {
	if (!token) {
		window.localStorage.removeItem(gis2TokenStorageId)
	} else {
		window.localStorage.setItem(gis2TokenStorageId, token)
	}
}

export const getGis2TokenFromStorage = () => {
	const token = window.localStorage.getItem(gis2TokenStorageId)
	const isUndefinedToken = token === '' || token === 'null'
	return isUndefinedToken ? null : token
}

export const generateGis2LoginToken = async (un, pw) => {
	const requestBody = joinQueryParams({
		username: un,
		password: pw,
		f: "json",
		expiration: 20160, // 2 weeks (mins)
		client: "referer",
		referer: window.location.origin,
	})

	const url =  'https://gis-portal.london.gov.uk/arcgis/sharing/rest/generateToken';
	return await axios.post(url, requestBody)
		.then((res) => {
			// The endpoint returns 200 status even if there is an error ¯\_(ツ)_/¯
			// Must check for presence of 'res.data.error' instead
			return res.data.error ? null : res.data.token
		})
}

export const isGis2TokenStillValid = async (token, tableToAuthAgainst, fieldToAuthAgainst) => {
	return doesGis2TokenPermitAccess(token, tableToAuthAgainst, fieldToAuthAgainst)
}

export const doesGis2TokenPermitAccess = async (token, tableToAuthAgainst, fieldToAuthAgainst) => {
	// May also be used to check if a token is still valid.

	if (!token) {
		return false
	}

	const authUrl = generateAuthCheckUrl(tableToAuthAgainst, fieldToAuthAgainst, token)
	return await checkAuthStatus(authUrl, fieldToAuthAgainst)
}

const generateAuthCheckUrl = (tableName, outField, token=null) => {
	if (!tableName || !outField) {
		throw new Error("tableName and outField required to check authorisation")
	}

    const encodedTableName = encodeURIComponent(tableName);

    return `https://gis.london.gov.uk/arcgis/rest/services/apps/${encodedTableName}/MapServer/0/query?where=authenticated+IS+NOT+NULL&f=pjson&token=${token}`;
};

const prepAuthQueryParams = (outField, token) => {
	const queryParams = {
		...authCheckUrlQueryParams,
		outFields: outField,
	}

	if (token) {
		queryParams.token = token	
	}

	return queryParams
}

const checkAuthStatus = async (authUrl, fieldToAuthAgainst) => {
	const hasFeatures = (features) => features && features.length > 0
	const authFieldIsAccessible = (features) => !!features[0].attributes[fieldToAuthAgainst]

	return await axios.get(authUrl)
		.then((res) => {
			if (res.data.error) {
				return false
			}
			const features = res.data.features
			return hasFeatures(features) && authFieldIsAccessible(features)
		})
}

const loginFuncs = {
	putGis2TokenIntoStorage,
	getGis2TokenFromStorage,
	generateGis2LoginToken,
	isGis2TokenStillValid,
	doesGis2TokenPermitAccess,
} 

export default loginFuncs